import React, { useEffect, useState } from "react";
import DrawerGoBackComponent from "./DrawerGoBackComponent";
import { CurrentDrawerOpenType, closeDrawer } from "state/slices/RightDrawerSlice";
import { DialogOpenType, openDialog } from "state/slices/DialogSlice";
import CMRTransformSliderComponent from "./CMRTransformSliderComponent";
import { useTypedSelector } from "hooks/store";
import * as modelsData from "assets/json/models.json";
import { Button } from "@mui/material";
import { useTypedDispatch } from "hooks/store";
import { builder } from "Classes/global";
import { dataEvents } from "Classes/Events";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { setPatientLocked } from "state/slices/SceneSlice";

type PatientAdjustment = {
    target: string;
    property: "position" | "rotation" | "scale";
    axis: "x" | "y" | "z";
    value: number;
};

const DrawerBedComponent = (): JSX.Element => {
    const dispatch = useTypedDispatch();

    const position = useTypedSelector((state) => state.Scene.selectedPatientPosition);

    const [bedHeadZ, setBedHeadZ] = useState(false);

    const [defaultLateralTilt, setDefaultLateralTilt] = useState(0);

    const [defaultIncline, setDefaultIncline] = useState(0);

    const [defaultHeight, setDefaultHeight] = useState(0.49);

    const [defaultHeadIncline, setDefaultHeadIncline] = useState(0);

    const [sceneLoaded, setSceneLoaded] = useState(0);

    useEffect(() => {
        const handleDataLoaded = (): void => {
            setSceneLoaded((value) => value + 1);
        };

        dataEvents.on("dataLoaded", handleDataLoaded);

        return () => {
            dataEvents.off("dataLoaded", handleDataLoaded);
        };
    }, []);

    useEffect(() => {
        if (!position) {
            return;
        }

        const modelData = modelsData.globalSettings.models.patientBed.find(
            (model) => model.name === position
        );

        const savedAdjustments =
            (
                builder?.room?.currentConfiguration?.patientPosition as {
                    adjustments?: PatientAdjustment[];
                }
            )?.adjustments ?? [];

        const getSavedValue = (
            target: string,
            property: PatientAdjustment["property"],
            axis: PatientAdjustment["axis"],
            fallback: number
        ): number => {
            const savedAdjustment = savedAdjustments.find(
                (adjustment) =>
                    adjustment.target === target &&
                    adjustment.property === property &&
                    adjustment.axis === axis
            );

            return savedAdjustment?.value ?? fallback;
        };

        setDefaultLateralTilt(getSavedValue("Waist", "rotation", "z", 0));

        setDefaultIncline(getSavedValue("Waist", "rotation", "x", modelData?.defaultIncline ?? 0));

        setDefaultHeight(getSavedValue("Waist", "position", "y", 0.49));

        setDefaultHeadIncline(getSavedValue("Center_Hinge", "rotation", "x", 0));

        setBedHeadZ(modelData?.BedHeadZ ?? false);
    }, [position, sceneLoaded]);

    const changePatient = async (): Promise<void> => {
        dispatch(closeDrawer(CurrentDrawerOpenType.Bed));

        dispatch(
            openDialog({
                isDialogOpen: true,
                type: DialogOpenType.ChangePatient,
            })
        );
    };

    const lockPatient = (): void => {
        builder.room.setPatientLocked(true);
        dispatch(setPatientLocked(true));
        dispatch(closeDrawer(CurrentDrawerOpenType.Bed));
    };

    return (
        <section className="bedComponent">
            <article>
                <CMRTransformSliderComponent
                    title="Lateral Tilt"
                    target="Waist"
                    property="rotation"
                    axis="z"
                    min={-20}
                    max={20}
                    step={1}
                    defaultValue={defaultLateralTilt}
                />

                <CMRTransformSliderComponent
                    title="Incline"
                    target="Waist"
                    property="rotation"
                    axis="x"
                    min={-20}
                    max={20}
                    step={1}
                    defaultValue={defaultIncline}
                />

                <CMRTransformSliderComponent
                    title="Height"
                    target="Waist"
                    property="position"
                    axis="y"
                    min={0.4}
                    max={0.55}
                    step={0.01}
                    defaultValue={defaultHeight}
                />
            </article>

            {bedHeadZ && (
                <article>
                    <CMRTransformSliderComponent
                        title="Head Incline"
                        target="Center_Hinge"
                        property="rotation"
                        axis="x"
                        min={-15}
                        max={0}
                        step={1}
                        defaultValue={defaultHeadIncline}
                    />
                </article>
            )}

            <article style={{ marginTop: "2rem" }}>
                <Button
                    onClick={lockPatient}
                    fullWidth
                    className="btn pinkRoundedButton"
                    startIcon={<LockOutlinedIcon />}
                >
                    Lock Patient
                </Button>
            </article>

            <article style={{ marginTop: "1rem" }}>
                <Button onClick={changePatient} fullWidth className="btn pinkRoundedButton">
                    Patient Models
                </Button>
            </article>

            <DrawerGoBackComponent classes="onlyDesktop" type={CurrentDrawerOpenType.Bed} />
        </section>
    );
};

export default DrawerBedComponent;
